import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { createStyles } from "antd-style";

import { Typography } from "@av/components/atoms/typography/Typography";
import { type AdminSettingsUserBase } from "../../../../../services/admin-settings/type";

import type { TFunction } from "i18next";

type EmailCellProps<TUser extends AdminSettingsUserBase> = {
  email: string;
  user: TUser;
  t: TFunction;
  testIdPrefix: string;
  getTestIdValue?: (user: TUser) => string;
};

const useStyles = createStyles(({ css, token }) => ({
  copyableEmail: css`
    align-items: center;
    border-radius: ${token.borderRadiusSM}px;
    display: inline-flex;
    line-height: ${token.lineHeight};
    padding: ${token.paddingXXS}px ${token.paddingXS}px;
    transition: background ${token.motionDurationMid};
    overflow-wrap: anywhere;
    white-space: normal;
    word-break: normal;

    &:hover {
      background: ${token.colorPrimaryBg};
    }

    .ant-typography-copy {
      color: ${token.colorPrimary};
      margin-inline-start: ${token.marginXXS}px;
    }
  `,
}));

export function EmailCell<TUser extends AdminSettingsUserBase>({
  email,
  user,
  t,
  testIdPrefix,
  getTestIdValue = (adminSettingsUser) => adminSettingsUser.userId,
}: EmailCellProps<TUser>) {
  const { styles } = useStyles();
  const testIdValue = getTestIdValue(user);

  return (
    <Typography.Text
      className={styles.copyableEmail}
      copyable={{
        icon: [
          <CopyOutlined data-testid={`${testIdPrefix}-email-copy-${testIdValue}`} key="copy" />,
          <CheckOutlined
            data-testid={`${testIdPrefix}-email-copied-${testIdValue}`}
            key="copied"
          />,
        ],
        text: email,
        tooltips: [t("common.components.copy"), t("common.components.copied")],
      }}
      testIdPrefix={`${testIdPrefix}-email-${testIdValue}`}
    >
      {email}
    </Typography.Text>
  );
}
